import React from 'react';
import classNames from 'classnames';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import FolderIcon from 'icons/Folder.icon';
import ResolvePopup from 'components/ResolvePopup';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { isMaximumSelected } from 'router/subrouters/DataRoom/utils';
import { getIsNodeFolder, getNodeMimeType } from 'utils';

import './StageFileRow.styles.scss';

type StageFileRowProps = {
  className?: string;
  node: DataRoomNode;
  meta: string;
  depth?: number;
  hasChevron?: boolean;
  isExpanded?: boolean;
  note?: string;
  // Sits before the icon, e.g. a drag handle and a number.
  prefix?: React.ReactNode;
  action: React.ReactNode;
  onClick?: () => void;
  // Turns the name into a button, e.g. to open the file.
  onNameClick?: () => void;
  // Dims the row and shows why on hover.
  disabledReason?: string;
};

const StageFileRow: React.FC<StageFileRowProps> = (props) => {
  const {
    className,
    node,
    meta,
    depth = 0,
    hasChevron,
    isExpanded,
    note,
    prefix,
    action,
    onClick,
    onNameClick,
    disabledReason,
  } = props;

  const { name, fileSecurity } = node;

  const isFolder = getIsNodeFolder(node);

  const classes = classNames(
    'stage-file-row',
    { clickable: !!onClick, 'stage-file-row--disabled': !!disabledReason },
    className,
  );

  const row = (
    <div
      className={classes}
      style={{ '--depth': depth } as React.CSSProperties}
      onClick={onClick}
    >
      {prefix}

      {hasChevron && (
        <span className="stage-file-row__chevron">
          {isFolder && (
            <ChevrondownIcon className={classNames({ expanded: isExpanded })} />
          )}
        </span>
      )}

      <span className="stage-file-row__icon">
        {isFolder ? (
          <FolderIcon className="folder-icon" />
        ) : (
          <MymeTypeIcon
            mimeType={getNodeMimeType(node)}
            isLocked={isMaximumSelected(fileSecurity)}
          />
        )}
      </span>

      <div className="stage-file-row__info">
        <p className="stage-file-row__name">
          {onNameClick ? (
            <button type="button" data-no-drag onClick={onNameClick}>
              {name}
            </button>
          ) : (
            name
          )}
        </p>
        <p className="stage-file-row__meta">{meta}</p>
        {note && <p className="stage-file-row__note">{note}</p>}
      </div>

      {/* The action handles its own clicks, so they never toggle the row.
          On a disabled row with no click of its own, a tap passes through to open the reason. */}
      <div
        className="stage-file-row__action"
        data-no-drag
        onClick={(e) => {
          if (onClick || !disabledReason) e.stopPropagation();
        }}
      >
        {action}
      </div>
    </div>
  );

  if (!disabledReason) return row;

  return (
    <ResolvePopup
      className="stage-file-row__popup"
      onHover
      popupContent={disabledReason}
    >
      {row}
    </ResolvePopup>
  );
};

export default StageFileRow;
